import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { getJson } from '@/lib/http';

type Assignment = {
  id: number;
  campaignId: number;
  storeId: number;
  packs: number;
  campaignName: string;
  retailerName: string;
  cycleId: number;
  cycleName: string;
  retailerId: number;
  status: string;
  taskId?: number;
  taskName?: string;
  rate?: number;
  earnings?: number;
  store?: { name?: string; address?: string };
  campaign?: {
    name?: string;
    bookingCycle?: { name?: string; retailer?: { name?: string } };
    campaignTypeKey?: string;
  };
};

type Operator = { id: number; name: string };
type Retailer = { id: number; name: string };
type BookingCycle = { id: number; retailerId: number; name: string };
type StoreTaskMapRow = { campaignId: number; storeId: number; taskId: number };
type TaskListRow = { id: number; name?: string; defaultRate?: number };
type OperatorTaskRate = { operatorId: number; taskId: number; rate?: number };
type OperatorDiscountRule = {
  id: number;
  name?: string;
  operatorId: number | null;
  taskId: number;
  campaignCountMin: number;
  campaignCountMax: number | null;
  effectiveRate?: number;
  isActive?: boolean;
};

export default function OperatorCycleReports() {
  const [selectedOperator, setSelectedOperator] = useState<number | null>(null);
  const [selectedRetailer, setSelectedRetailer] = useState<number | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<number | null>(null);

  // Load operators list for dropdown
  const { data: operators = [], isLoading: operatorsLoading } = useQuery<Operator[]>({
    queryKey: ['/api/users/operators'],
    queryFn: () => getJson<Operator[]>('/api/users/operators'),
  });



  // Load retailers list for dropdown
  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ['/api/retailers'],
    queryFn: () => getJson<Retailer[]>('/api/retailers'),
  });

  // Load booking cycles for selected retailer
  const { data: allCycles = [] } = useQuery<BookingCycle[]>({
    queryKey: ['/api/booking-cycles'],
    queryFn: () => getJson<BookingCycle[]>('/api/booking-cycles'),
  });

  // Filter cycles by selected retailer
  const availableCycles = useMemo(() => {
    if (!selectedRetailer) return [];
    return allCycles.filter((cycle: any) => cycle.retailerId === selectedRetailer);
  }, [allCycles, selectedRetailer]);

  // Load assignments for the selected operator - only fetch when operator is selected
  const { data: assignments = [], isLoading: assignmentsLoading, refetch: refetchAssignments } = useQuery<Assignment[]>({
    queryKey: ['/api/admin/assignments', selectedOperator, selectedRetailer, selectedCycle],
    queryFn: async () => {
      let url = `/api/admin/assignments?operatorId=${selectedOperator}`;
      if (selectedRetailer) url += `&retailerId=${selectedRetailer}`;
      if (selectedCycle) url += `&cycleId=${selectedCycle}`;
      const result = await fetch(url, { credentials: 'omit' }).then(r => r.json());
      return Array.isArray(result) ? result as Assignment[] : [];
    },
    enabled: !!selectedOperator, // Only fetch when operator is selected
    staleTime: 30_000, // 30 seconds - short cache for real-time updates
  });

  // Load storeTaskMap for task ID lookup
  const { data: storeTasks = [] } = useQuery<StoreTaskMapRow[]>({
    queryKey: ['/api/campaigns/storeTaskMap'],
    queryFn: () => getJson<StoreTaskMapRow[]>('/api/campaigns/storeTaskMap'),
    staleTime: 300_000, // 5 minutes
  });

  // Load task list for task name lookup
  const { data: fullTaskList = [] } = useQuery<TaskListRow[]>({
    queryKey: ['/api/tasks'],
    queryFn: () => getJson<TaskListRow[]>('/api/tasks'),
    staleTime: 300_000, // 5 minutes
  });

  // Load operator task rates for value calculation
  const { data: operatorTaskRates = [] } = useQuery<OperatorTaskRate[]>({
    queryKey: ['/api/admin/operator-task-rates'],
    queryFn: () => getJson<OperatorTaskRate[]>('/api/admin/operator-task-rates'),
    staleTime: 300_000, // 5 minutes
  });

  // Load operator discount rules for value calculation
  const { data: operatorDiscountRules = [] } = useQuery<OperatorDiscountRule[]>({
    queryKey: ['/api/admin/operator-discount-rules-enhanced'],
    queryFn: () => getJson<OperatorDiscountRule[]>('/api/admin/operator-discount-rules-enhanced'),
    staleTime: 300_000, // 5 minutes
  });

  // Load operator-specific task rules for value calculation
  const { data: operatorTaskRules = [] } = useQuery<OperatorDiscountRule[]>({
    queryKey: ['/api/admin/operator-task-rules', selectedOperator],
    queryFn: async () => {
      if (!selectedOperator) return [];
      const response = await fetch(`/api/admin/operator-task-rules?operatorId=${selectedOperator}`, {
        credentials: 'omit',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch operator task rules');
      }
      const data = await response.json();
      return Array.isArray(data) ? data as OperatorDiscountRule[] : [];
    },
    enabled: !!selectedOperator,
    staleTime: 300_000, // 5 minutes
  });

  // Debug operator discount rules data
  useEffect(() => {
    if (operatorDiscountRules.length > 0) {
      console.log('Loaded operator discount rules:', operatorDiscountRules);
    }
  }, [operatorDiscountRules]);

  // Build lookup table (campaignId-storeId → taskId)
  const taskLookup = storeTasks.reduce((acc: any, task: any) => {
    acc[`${task.campaignId}-${task.storeId}`] = task.taskId;
    return acc;
  }, {});

  // Server-side filtering is now handled by the API call, so we use assignments directly
  // Handle error responses properly
  const filteredAssignments = Array.isArray(assignments) ? assignments : [];

  // Group assignments by store
  const groupedAssignments = useMemo(() => {
    const grouped: { [storeId: string]: Assignment[] } = {};
    
    filteredAssignments.forEach(assignment => {
      const storeId = assignment.storeId.toString();
      if (!grouped[storeId]) {
        grouped[storeId] = [];
      }
      grouped[storeId].push(assignment);
    });
    
    return grouped;
  }, [filteredAssignments]);

  // Calculate task value for an assignment
  const calculateTaskValue = (assignment: Assignment, taskId: number): number => {
    if (!selectedOperator || !taskId) return 0;

    // Get base task rate for this operator and task
    const taskRate = operatorTaskRates.find((rate: any) => 
      rate.operatorId === selectedOperator && rate.taskId === taskId
    );
    
    if (!taskRate) {
      // Fall back to default task rate if no operator-specific rate exists
      const defaultTask = fullTaskList.find(task => task.id === taskId);
      if (!defaultTask) return 0;
      return defaultTask.defaultRate || 0;
    }

    let baseRate = taskRate.rate || 0;

    // Apply discount rules based on TASK count in the same store and cycle
    const storeAssignments = filteredAssignments.filter(a =>
      a.storeId === assignment.storeId && 
      a.cycleId === assignment.cycleId
    );
    
    // Count how many times THIS SPECIFIC TASK is performed in this store/cycle
    const taskCount = storeAssignments.filter(a => {
      const key = `${a.campaignId}-${a.storeId}`;
      return taskLookup[key] === taskId;
    }).length;
    
    // Find applicable discount rule (prioritize operator-specific rules over global rules)
    // First check operator-specific task rules
    // For task rules, we want the highest threshold that doesn't exceed the task count
    let applicableRules = operatorTaskRules.filter((rule) => 
      rule.taskId === taskId &&
      rule.campaignCountMin <= taskCount && 
      rule.isActive
    );
    
    // If no operator-specific rules found, check global rules
    if (applicableRules.length === 0) {
      applicableRules = operatorDiscountRules.filter((rule) => 
        (rule.operatorId === selectedOperator || rule.operatorId == null) && 
        rule.taskId === taskId &&
        rule.campaignCountMin <= taskCount && 
        (rule.campaignCountMax == null || rule.campaignCountMax >= taskCount)
      );
    }

    // Sort by specificity (operator-specific first, then by highest campaign count threshold)
    const applicableRule = applicableRules.sort((a, b) => {
      // Prioritize operator-specific rules
      if (a.operatorId === selectedOperator && b.operatorId === null) return -1;
      if (a.operatorId === null && b.operatorId === selectedOperator) return 1;
      
      // Then prioritize highest campaign count threshold (most specific rule)
      return b.campaignCountMin - a.campaignCountMin;
    })[0];

    // Debug logging for task value calculation
    const assignmentDebugId = String(assignment.id);
    if (assignmentDebugId === 'virtual-92-19' || assignmentDebugId === 'virtual-85-19') { // Debug first few assignments
      console.log(`Task value calculation for assignment ${assignment.id}:`, {
        taskId,
        baseRate,
        taskCount,
        storeId: assignment.storeId,
        cycleId: assignment.cycleId,
        selectedOperator,
        operatorTaskRules: operatorTaskRules.filter(r => r.taskId === taskId),
        availableGlobalRules: operatorDiscountRules.filter(r => r.taskId === taskId),
        applicableRules,
        applicableRule: applicableRule ? {
          id: applicableRule.id,
          name: applicableRule.name,
          effectiveRate: applicableRule.effectiveRate,
          campaignCountMin: applicableRule.campaignCountMin,
          campaignCountMax: applicableRule.campaignCountMax,
          operatorId: applicableRule.operatorId,
          isOperatorSpecific: !!applicableRule.operatorId
        } : null,
      });
    }

    let finalValue = baseRate;
    
    if (applicableRule && applicableRule.effectiveRate) {
      finalValue = applicableRule.effectiveRate;
    }

    // Debug logging for return value
    if (assignmentDebugId === 'virtual-92-19' || assignmentDebugId === 'virtual-85-19') {
      console.log(`Final calculated value for assignment ${assignment.id}:`, {
        taskId,
        baseRate,
        applicableRule: applicableRule ? applicableRule.effectiveRate : null,
        finalValue
      });
    }

    return finalValue;
  };

  // Reset cycle when retailer changes
  const handleRetailerChange = (retailerId: string) => {
    const id = retailerId ? Number(retailerId) : null;
    setSelectedRetailer(id);
    setSelectedCycle(null); // Reset cycle when retailer changes
  };

  if (!selectedOperator) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Operator Cycle Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="operator-select">Select Operator:</Label>
              <Select value={selectedOperator?.toString() || ''} onValueChange={value => setSelectedOperator(value ? Number(value) : null)}>
                <SelectTrigger id="operator-select" className="max-w-xs">
                  <SelectValue placeholder="— choose operator —" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator: any) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              Please select an operator to view their cycle reports.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (assignmentsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Operator Cycle Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filter Section */}
          <div className="flex items-end gap-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
              <div>
              <Label htmlFor="operator-select">Operator:</Label>
              <Select value={selectedOperator?.toString() || ''} onValueChange={value => setSelectedOperator(value ? Number(value) : null)}>
                <SelectTrigger id="operator-select">
                  <SelectValue placeholder="— choose operator —" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator: any) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="retailer-select">Retailer:</Label>
              <Select value={selectedRetailer?.toString() || ''} onValueChange={handleRetailerChange}>
                <SelectTrigger id="retailer-select">
                  <SelectValue placeholder="— all retailers —" />
                </SelectTrigger>
                <SelectContent>
                  {retailers.map((retailer: any) => (
                    <SelectItem key={retailer.id} value={retailer.id.toString()}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="cycle-select">Retailer Cycle:</Label>
              <Select 
                value={selectedCycle?.toString() || ''} 
                onValueChange={value => setSelectedCycle(value ? Number(value) : null)}
                disabled={!selectedRetailer}
              >
                <SelectTrigger id="cycle-select">
                  <SelectValue placeholder={selectedRetailer ? "— all cycles —" : "— select retailer first —"} />
                </SelectTrigger>
                <SelectContent>
                  {availableCycles.map((cycle: any) => (
                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
            {/* Refresh Button */}
            <Button 
              onClick={() => refetchAssignments()} 
              variant="outline" 
              size="sm"
            >
              Refresh Data
            </Button>
          </div>

          {/* Results Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Showing {filteredAssignments.length} assignments
                {selectedRetailer && ` for ${retailers.find((r: any) => r.id === selectedRetailer)?.name}`}
                {selectedCycle && ` in cycle ${availableCycles.find((c: any) => c.id === selectedCycle)?.name}`}
              </p>
              {(selectedRetailer || selectedCycle) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedRetailer(null);
                    setSelectedCycle(null);
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}
            </div>
            
            {filteredAssignments.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No assignments found for the selected filters.
              </p>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedAssignments).map(([storeId, assignments]) => {
                  const firstAssignment = assignments[0];
                  const storeName = firstAssignment.store?.name || 'Unknown Store';
                  const storeAddress = firstAssignment.store?.address || '';
                  
                  // Calculate total value for this store and task counts
                  const totalStoreValue = assignments.reduce((sum, assignment) => {
                    const key = `${assignment.campaignId}-${assignment.storeId}`;
                    const taskId = taskLookup[key];
                    const taskValue = taskId ? calculateTaskValue(assignment, taskId) : 0;
                    return sum + taskValue;
                  }, 0);
                  
                  // Group assignments by task ID for count display
                  const taskCounts = assignments.reduce((acc, assignment) => {
                    const key = `${assignment.campaignId}-${assignment.storeId}`;
                    const taskId = taskLookup[key];
                    if (taskId) {
                      const taskName = fullTaskList.find(task => task.id === taskId)?.name || `Task ${taskId}`;
                      const taskKey = `${taskId}-${taskName}`;
                      acc[taskKey] = (acc[taskKey] || 0) + 1;
                    }
                    return acc;
                  }, {} as Record<string, number>);
                  
                  return (
                    <div key={storeId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{storeName}</h3>
                          <p className="text-sm text-muted-foreground">
                            Store ID: {storeId} {storeAddress && `• ${storeAddress}`}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(taskCounts).map(([taskKey, count]) => {
                              const [taskId, taskName] = taskKey.split('-');
                              return (
                                <Badge key={taskKey} variant="secondary" className="text-xs">
                                  {count}x {taskName}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs mb-1">
                            {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
                          </Badge>
                          <div className="text-sm font-medium text-green-600">
                            Total: €{totalStoreValue.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {assignments.map((assignment) => {
                          const key = `${assignment.campaignId}-${assignment.storeId}`;
                          const taskId = taskLookup[key];
                          const taskName = taskId ? fullTaskList.find(task => task.id === taskId)?.name : null;
                          const taskValue = taskId ? calculateTaskValue(assignment, taskId) : 0;
                          
                          return (
                            <div key={assignment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                              <div className="flex-1">
                                <div className="font-medium">{assignment.campaign?.name || 'Unknown Campaign'}</div>
                                <div className="text-sm text-muted-foreground">
                                  {assignment.campaign?.bookingCycle?.retailer?.name || 'Unknown Retailer'}
                                  {assignment.campaign?.bookingCycle?.name && ` • Cycle: ${assignment.campaign.bookingCycle.name}`}
                                  {assignment.campaign?.campaignTypeKey && ` • Type: ${assignment.campaign.campaignTypeKey}`}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Campaign ID: {assignment.campaignId}
                                </div>
                              </div>
                              
                              <div className="text-right">
                                <div className="space-y-1">
                                  {taskId ? (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="default">Task ID: {taskId}</Badge>
                                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                                          €{taskValue.toFixed(2)}
                                        </Badge>
                                      </div>
                                      {taskName && (
                                        <div className="text-sm text-muted-foreground font-medium">
                                          {taskName}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <Badge variant="destructive">No mapping</Badge>
                                  )}
                                  
                                  <div className="text-sm text-muted-foreground">
                                    Status: {assignment.status}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
